import React from 'react';
import { ExternalLink, Ticket, Plane, Bed, Car, Coffee, TrendingUp } from 'lucide-react';
import { trackAffiliateClick } from '../utils/analytics';
import { useTheme } from '../contexts/ThemeContext';

const AffiliateSection = () => {
  const { teamColors, selectedTeam } = useTheme();

  const ticketPartners = [
    {
      name: "StubHub",
      icon: Ticket,
      description: "Official ticket marketplace with buyer guarantee",
      savings: "Up to 15% off",
      url: "https://stubhub.prf.hn/l/W4EJzJX",
      color: "bg-blue-600"
    },
    {
      name: "Ticketmaster",
      icon: Ticket,
      description: "Primary source for official game tickets",
      savings: "Face value + fees",
      url: "https://ticketmaster.evyy.net/c/4976406/264167/4272?u=https%3A%2F%2Fwww.ticketmaster.com",
      color: "bg-indigo-600"
    },
    {
      name: "SeatGeek",
      icon: Ticket,
      description: "Transparent pricing with Deal Score ratings",
      savings: "Best price guarantee",
      url: "https://seatgeek.pxf.io/9g0Xn4",
      color: "bg-green-600"
    },
    {
      name: "Vivid Seats",
      icon: Ticket,
      description: "Resale tickets with rewards",
      savings: "Promo codes",
      url: "https://vivid-seats.pxf.io/Vm3V23",
      color: "bg-red-600"
    }
  ];

  const travelPartners = [
    {
      name: "Expedia",
      icon: Plane,
      description: "Complete travel packages - flights, hotels, cars",
      savings: "Bundle & save up to 30%",
      url: "https://expedia.com/affiliate/QloXX5p",
      color: "bg-yellow-600"
    },
    {
      name: "Travelocity",
      icon: Plane,
      description: "Flights and vacation packages",
      savings: "Seasonal promos",
      url: "https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704",
      color: "bg-cyan-600"
    },
    {
      name: "Hotels.com",
      icon: Bed,
      description: "Hotel deals and rewards",
      savings: "Member discounts",
      url: "https://www.hotels.com/affiliates/hotelscom-home.VsBKzRM",
      color: "bg-rose-600"
    },
    {
      name: "VRBO",
      icon: Bed,
      description: "Vacation rentals perfect for fan groups",
      savings: "Group discounts available",
      url: "https://vrbo.com/affiliate/oWiAOm8",
      color: "bg-purple-600"
    },
    {
      name: "Orbitz",
      icon: Plane,
      description: "Flights and hotels",
      savings: "Orbucks rewards",
      url: "https://prf.hn/click/camref:1011lTcBz/creativeref:1011l22206",
      color: "bg-indigo-700"
    },
    {
      name: "Tripadvisor",
      icon: Plane,
      description: "Reviews and travel deals",
      savings: "Compare prices",
      url: "https://www.tkqlhce.com/click-100918385-15668034",
      color: "bg-emerald-700"
    }
  ];

  const experiencePartners = [
    {
      name: "OpenTable",
      icon: Coffee,
      description: "Reserve tables at the best restaurants near stadiums",
      savings: "Exclusive reservations",
      url: "https://opentable.com",
      color: "bg-orange-600"
    }
  ];

  const PartnerCard = ({ partner, index }) => (
    <a
      href={partner.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackAffiliateClick(partner.name, partner.url, { location: 'AffiliateSection' })}
      className="group block relative overflow-hidden"
    >
      <div 
        className="card-field p-6 h-full transition-all duration-300 group-hover:shadow-2xl group-hover:scale-105 relative"
        style={{
          borderLeft: `4px solid ${teamColors.accent}`,
          borderBottom: `2px solid ${teamColors.accent}30`
        }}
      >
        {/* Football-themed accent instead of circle */}
        <div 
          className="absolute top-2 right-2 w-10 h-7 football-shape opacity-15 group-hover:opacity-25 transition-all duration-300"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Corner yard line marker */}
        <div 
          className="absolute bottom-0 left-0 w-8 h-8 opacity-10 group-hover:opacity-15 transition-all duration-300"
          style={{ 
            backgroundColor: teamColors.accent,
            clipPath: 'polygon(0 0, 100% 100%, 0 100%)'
          }}
        ></div>

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-200"
              style={{ backgroundColor: teamColors.accent }}
            >
              <partner.icon size={24} style={{ color: teamColors.primary }} />
            </div>
            <ExternalLink size={16} className="text-gray-400 group-hover:text-gray-600 transition-colors" />
          </div>
          
          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
            {partner.name}
          </h3>
          <p className="text-gray-600 text-sm mb-3">{partner.description}</p>
          
          <div className="flex items-center justify-between">
            <span 
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white"
              style={{ backgroundColor: teamColors.primary }}
            >
              <TrendingUp size={12} className="mr-1" />
              {partner.savings}
            </span>
          </div>
        </div>
      </div>
    </a>
  );

  return (
    <section 
      className="py-16 relative overflow-hidden football-field-subtle pattern-football-lines"
      style={{
        background: selectedTeam 
          ? `linear-gradient(to bottom, ${teamColors.primary} 0%, ${teamColors.secondary} 50%, ${teamColors.primary} 100%)`
          : 'linear-gradient(to bottom, #0ea5e9 0%, #f97316 50%, #0ea5e9 100%)'
      }}
    >
      {/* Football field decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Yard line markings */}
        <div 
          className="absolute top-0 left-1/6 w-1 h-full opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute top-0 left-3/6 w-2 h-full opacity-30"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute top-0 left-5/6 w-1 h-full opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Goal posts */}
        <div 
          className="absolute top-20 left-20 w-8 h-8 goalpost opacity-40"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-20 right-20 w-8 h-8 goalpost opacity-40"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Football field hash marks */}
        <div 
          className="absolute top-16 left-0 w-full h-1 opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-16 left-0 w-full h-1 opacity-25"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        
        {/* Football decorations */}
        <div 
          className="absolute top-24 right-24 w-12 h-8 football-shape football-laces opacity-30 animate-float"
          style={{ backgroundColor: teamColors.accent }}
        ></div>
        <div 
          className="absolute bottom-24 left-24 w-10 h-7 football-shape football-laces opacity-30 animate-float"
          style={{ 
            backgroundColor: teamColors.accent,
            animationDelay: '2s'
          }}
        ></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header with team accent */}
        <div className="text-center mb-16">
          <div className="flex justify-center items-center mb-4">
            <div 
              className="w-16 h-1 rounded-full mr-2"
              style={{ backgroundColor: teamColors.accent }}
            ></div>
            <div 
              className="w-8 h-1 rounded-full mr-2"
              style={{ backgroundColor: teamColors.accent }}
            ></div>
            <div 
              className="w-4 h-1 rounded-full"
              style={{ backgroundColor: teamColors.accent }}
            ></div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">
            Official Travel Partners
          </h2>
          <p className="text-xl text-white opacity-90 max-w-3xl mx-auto drop-shadow-md">
            Book with confidence through our verified partners. Every booking supports the 40 Bags community.
          </p>
        </div>

        {/* Game Tickets Section */}
        <div className="mb-12">
          <div className="flex items-center mb-8">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg mr-4"
              style={{ backgroundColor: teamColors.accent }}
            >
              <Ticket size={24} style={{ color: teamColors.primary }} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white drop-shadow-md">Game Tickets</h3>
              <p className="text-white opacity-80 drop-shadow-sm">Secure your seats with trusted ticket platforms</p>
            </div>
            {/* Section accent */}
            <div className="ml-auto hidden md:block">
              <div 
                className="w-24 h-1 rounded-full"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ticketPartners.map((partner, index) => (
              <PartnerCard key={partner.name} partner={partner} index={index} />
            ))}
          </div>
        </div>

        {/* Travel & Accommodation Section */}
        <div className="mb-12">
          <div className="flex items-center mb-8">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg mr-4"
              style={{ backgroundColor: teamColors.accent }}
            >
              <Plane size={24} style={{ color: teamColors.primary }} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white drop-shadow-md">Travel & Stays</h3>
              <p className="text-white opacity-80 drop-shadow-sm">Complete travel solutions for your game day trip</p>
            </div>
            {/* Section accent */}
            <div className="ml-auto hidden md:block">
              <div 
                className="w-24 h-1 rounded-full"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {travelPartners.map((partner, index) => (
              <PartnerCard key={partner.name} partner={partner} index={index} />
            ))}
          </div>
        </div>

        {/* Game Day Experiences Section */}
        <div>
          <div className="flex items-center mb-8">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center shadow-lg mr-4"
              style={{ backgroundColor: teamColors.accent }}
            >
              <Coffee size={24} style={{ color: teamColors.primary }} />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white drop-shadow-md">Game Day Experiences</h3>
              <p className="text-white opacity-80 drop-shadow-sm">Make your trip unforgettable with local experiences</p>
            </div>
            {/* Section accent */}
            <div className="ml-auto hidden md:block">
              <div 
                className="w-24 h-1 rounded-full"
                style={{ backgroundColor: teamColors.accent }}
              ></div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {experiencePartners.map((partner, index) => (
              <PartnerCard key={partner.name} partner={partner} index={index} />
            ))}
          </div>
        </div>

        {/* Partner Benefits Banner */}
        <div 
          className="mt-16 p-8 rounded-2xl relative overflow-hidden shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${teamColors.primary} 0%, ${teamColors.secondary} 100%)`
          }}
        >
          {/* Football field pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 football-field-subtle"></div>
          </div>
          
          <div className="relative z-10 text-center text-white">
            <h3 className="text-2xl font-bold mb-4 drop-shadow-lg">Why Book Through Our Partners?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold mb-2">💰</div>
                <h4 className="font-semibold mb-2">Best Prices</h4>
                <p className="text-sm opacity-90">Exclusive discounts and group rates</p>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">🛡️</div>
                <h4 className="font-semibold mb-2">Protected Bookings</h4>
                <p className="text-sm opacity-90">All purchases backed by trusted partner guarantees</p>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">🤝</div>
                <h4 className="font-semibold mb-2">Support the Community</h4>
                <p className="text-sm opacity-90">Your bookings help us keep 40bags free for all fans</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AffiliateSection; 